<template>
    <div class="projects" :id="id">
        <div v-for="segment in segments" :key="segment.id" class="result-segment">
            <div
                v-for="project in segment.projects"
                :key="project.id"
                class="project"
            >
                <h5 class="title">
                    <a :href="projectUrl(project)" data-bs-toggle="collapse" :data-bs-target="'#p-' + project.id">
                        {{ project.content.title[lang] || project.content.title[fallbackLang] }}
                    </a>
                </h5>

                <ul class="list-unstyled inst-leading">
                    <li v-for="host in institutionsAs('host', project.content.institutions)" :key="host.id">
                        {{ institutionLabel(host) }}
                    </li>
                </ul>

                <ul class="list-inline">
                    <li v-for="tag in categoryTags(project.content.taxonomy)" :key="tag.id" class="list-inline-item">
                        <span class="label label-keyword me-1">
                            {{ tag?.title[lang] ?? '[' + tag.id + ']' }}
                        </span>
                    </li>
                </ul>

                <div class="d-flex justify-content-between mb-1">
                    <div class="ms-auto">
                        <a :href="'#p-' + project.id" data-bs-toggle="collapse">
                            <span class="arrow">↓</span> Mehr
                        </a>
                    </div>
                </div>

                <div :id="'p-' + project.id" class="details collapse">
                    <project-sheet :project="project" :categories="categories" :taxonomy="taxonomy" />
                </div>

            </div>
        </div>

        <!-- LOAD MORE -->
        <div v-if="hasMore" class="d-flex justify-content-center">
            <button class="btn btn-outline-secondary" @click="loadMore">
                <strong>{{ trans('result.more') }}</strong>
                <div v-if="loading" class="spinner-border spinner-border-sm ms-2" role="status">
                    <span class="visually-hidden">...</span>
                </div>
            </button>
        </div>

    </div>
</template>

<script>

import ProjectSheet from './ProjectSheet.vue'
import { Collapse } from 'bootstrap'
import { merge } from 'lodash'
import axios from 'axios'
import { trans, currentLang, basePath, dataUrl } from './AppUtils'
import { toSearch } from './FilterModel'

const HTTP = axios.create()

export default {

    emits: [
        'offset'
    ],

    components: {
        ProjectSheet
    },

    props: {
        id: {
            type: String,
            default: () => "project-result-list"
        },
        projects: {
            type: Array,
            default: () => [],
        },
        taxonomy: {
            type: Object,
            default: () => ({}),
        },
        query: {
            type: Object,
            default: () => ({}),
        },
        stats: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            loading: false,
            lang: currentLang(),
            segments: [
                {id: 1, projects: this.projects}
            ]
        }
    },

    computed: {

        fallbackLang() {
            return (this.lang == 'de') ? 'en' : 'de'
        },

        hasMore() {
            const limit = this.query.limit ?? 10
            const offset = limit * (this.segments.length)
            const count = this.stats.projects.matches ?? 0
            return (offset < count)
        },

        categories() {
            return this.buildCategoryMap(this.taxonomy)
        }
    },

    watch: {
        projects(data) {
            this.segments = [{id: 1, projects: data}]
        }
    },

    mounted() {
        for (node of this.$el.querySelectorAll('.collapse')) {
            new Collapse(node)
        }
    },

    methods: {

        trans,

        institutionsAs(role, institutions) {
            const selection = []
            for (const inst of institutions) {
                const roles = inst.roles
                if (roles.includes(role)) {
                    selection.push(inst)
                }
            }
            return selection
        },

        institutionLabel(inst) {
            let label = ""
            if (inst.parent) {
                label += inst.parent + " – "
            }
            label += inst.name
            if (inst.abbr) {
                label += " (" + inst.abbr + ")"
            }
            return label
        },

        buildCategoryMap(nodes, target = {}) {
            for (const name in nodes) {
                const node = nodes[name]
                const info = {id: node.id, title: node.title }
                target[node.id] = info
                this.buildCategoryMap(node.children || [], target)
            }
            return target
        },

        projectUrl(project) {
            return basePath(this.lang + '/projects/' + project.id)
        },

        categoryTags(taxonomy) {
            const tags = []
            for (const t of this.taxonomy['research-fields'].children) {
                if (taxonomy.includes(t.id)) {
                    tags.push(t)
                }
            }
            for (const t of this.taxonomy['topics'].children) {
                if (taxonomy.includes(t.id)) {
                    tags.push(t)
                }
            }
            return tags
        },

        async loadMore() {
            const query = {}
            merge(query, this.query)
            query.limit = 10
            query.offset = query.limit * this.segments.length
            query.rel = 'none'

            const search = new URLSearchParams(toSearch(query))
            const source = dataUrl("map", search)

            this.loading = true
            try {
                const id = this.segments.length + 1
                const result = await HTTP.get(source.href)
                this.segments.push({id: id, projects: result.data.projects})
                this.$emit('offset', query.offset)
            } catch (error) {
                console.warn(error)
            }
            this.loading = false
        }

    }
}
</script>
